import { Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";
import type { Editor } from "@tiptap/react";
import type { FrontMatterFieldAttrs } from "@workith/editor-schema";
import { FRONT_MATTER_FIELD_NODE_NAME, FRONT_MATTER_NODE_NAME } from "@workith/editor-schema";

/**
 * 새 필드는 원문 attrs를 모두 비워 두어 공유 직렬화기가 "수정되지 않은 원문"이 아니라
 * 새 필드로 판단하고 표준 YAML로 출력하게 한다.
 */
const NEW_FIELD_ATTRS: FrontMatterFieldAttrs = {
  key: "새 필드",
  valueKind: "scalar",
  scalarType: "string",
  originalKey: "",
  originalCanonicalValue: "",
  originalSource: "",
};

/**
 * 위치가 문서 범위를 벗어나거나 노드 경계가 아니거나 기대한 타입이 아니면 `null`을 돌려준다.
 * 모든 명령은 이 검사를 통과했을 때만 정확히 한 번 dispatch한다.
 */
function nodeOfTypeAt(editor: Editor, pos: number, typeName: string): ProseMirrorNode | null {
  const { doc } = editor.state;
  if (!Number.isInteger(pos) || pos < 0 || pos >= doc.content.size) return null;
  const node = doc.nodeAt(pos);
  return node?.type.name === typeName ? node : null;
}

export function replaceFrontMatterFieldText(editor: Editor, pos: number, text: string): boolean {
  const node = nodeOfTypeAt(editor, pos, FRONT_MATTER_FIELD_NODE_NAME);
  if (!node) return false;
  const content = text.length > 0 ? Fragment.from(editor.schema.text(text)) : Fragment.empty;
  editor.view.dispatch(editor.state.tr.replaceWith(pos + 1, pos + node.nodeSize - 1, content));
  return true;
}

export function updateFrontMatterFieldAttrs(
  editor: Editor,
  pos: number,
  patch: Partial<FrontMatterFieldAttrs>
): boolean {
  const node = nodeOfTypeAt(editor, pos, FRONT_MATTER_FIELD_NODE_NAME);
  if (!node) return false;
  editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...patch }));
  return true;
}

export function appendFrontMatterField(editor: Editor, cardPos: number): boolean {
  const card = nodeOfTypeAt(editor, cardPos, FRONT_MATTER_NODE_NAME);
  const fieldType = editor.schema.nodes[FRONT_MATTER_FIELD_NODE_NAME];
  if (!card || !fieldType) return false;
  editor.view.dispatch(
    editor.state.tr.insert(cardPos + card.nodeSize - 1, fieldType.create({ ...NEW_FIELD_ATTRS }))
  );
  return true;
}

export function deleteFrontMatterField(editor: Editor, pos: number): boolean {
  const node = nodeOfTypeAt(editor, pos, FRONT_MATTER_FIELD_NODE_NAME);
  if (!node) return false;
  editor.view.dispatch(editor.state.tr.delete(pos, pos + node.nodeSize));
  return true;
}

/**
 * 카드 앞뒤 어느 방향의 요청이든 카드 "뒤" 본문으로 보낸다. 카드는 문서 절대 선두에만
 * 존재해야 하므로 카드 앞에서의 Enter/화살표가 본문을 선두에 만들지 못하게 하기 위함이다.
 * 뒤에 본문이 없으면 같은 transaction에서 paragraph를 만든 뒤 그 안으로 focus한다.
 */
export function focusAfterFrontMatter(editor: Editor, cardPos: number): boolean {
  const card = nodeOfTypeAt(editor, cardPos, FRONT_MATTER_NODE_NAME);
  const paragraphType = editor.schema.nodes.paragraph;
  if (!card || !paragraphType) return false;
  const after = cardPos + card.nodeSize;
  let tr = editor.state.tr;
  if (after >= tr.doc.content.size) {
    tr = tr.insert(after, paragraphType.create());
  }
  tr = tr.setSelection(TextSelection.near(tr.doc.resolve(after + 1), 1)).scrollIntoView();
  editor.view.dispatch(tr);
  return true;
}
